import { useMemo } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { useIntl } from 'react-intl'
import { mdiFolderOutline } from '@mdi/js'

import { Table } from 'components/Table'
import type { TableHeader } from 'components/Table/types'
import type { PendingJobColumnType } from 'features/domains/types'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { sortByInsensitiveString } from 'components/Table/utils'

import { pendingJobsTableMessages } from './messages'
import { StyledJobFolderIcon } from './styles'
import { PENDING_JOBS_HEADERS } from '../DomainPendingJobs/consts'
import MobilePendingJobComponent from '../DomainMobileJobsComponents/MobilePendingJobComponent'
import { DomainJobsTableContainer } from 'features/domains/styles'
import NotFoundBlock from 'components/NotFoundBlock'
import { makePublicUrl } from 'app/utils/publicUrl'
import { jobsMainMessages } from 'features/jobs/components/JobsMain/messages'
import { commonMessages } from 'app/i18n/messages'

const JobNameCell = (cell: Cell<PendingJobColumnType>) => {
  const job = cell.row.original
  const jobName = job.name

  return (
    <>
      <StyledJobFolderIcon path={mdiFolderOutline} size={1} />
      <b>{jobName}</b>
    </>
  )
}

const LastUpdateCell = (cell: Cell<PendingJobColumnType>) => {
  const job = cell.row.original
  const updated = job.updated

  return <IntlFormattedDate date={updated} withTime />
}

interface DomainPendingJobsTableProps {
  data: PendingJobColumnType[]
  headers: Record<PENDING_JOBS_HEADERS, TableHeader>
  defaultSortBy: SortingRule<PendingJobColumnType>
  sortBy: SortingRule<PendingJobColumnType>
  setSortBy: (sortBy: SortingRule<PendingJobColumnType>) => void
  searchValue?: string
}

function DomainPendingJobsTable(props: DomainPendingJobsTableProps) {
  const { data, defaultSortBy, sortBy, setSortBy, searchValue } = props

  const { formatMessage } = useIntl()

  const columns: readonly Column<PendingJobColumnType>[] = useMemo(
    () => [
      {
        Header: formatMessage(pendingJobsTableMessages.jobName),
        accessor: 'name',
        Cell: JobNameCell,
        percentWidth: '20%',
        sortType: sortByInsensitiveString,
      },
      {
        Header: formatMessage(pendingJobsTableMessages.updated),
        accessor: 'updated',
        Cell: LastUpdateCell,
      },
    ],
    [formatMessage]
  )

  return (
    <DomainJobsTableContainer>
      <Table
        $shouldRowBeLink
        columns={columns}
        data={data}
        defaultSortBy={defaultSortBy}
        sortBy={sortBy}
        setSortBy={setSortBy}
        searchValue={searchValue}
        mobileViewComponent={MobilePendingJobComponent}
        NoResultsComponent={
          <NotFoundBlock
            imgSrc={makePublicUrl(
              'images/illustrations/homes-illustration.svg'
            )}
            title={formatMessage(jobsMainMessages.noJobsFound)}
            text={formatMessage(commonMessages.fixCriteria)}
          />
        }
      />
    </DomainJobsTableContainer>
  )
}

export default DomainPendingJobsTable
